import React, { useEffect, useState } from "react";
import axios from "../../api/axiosinstance";
import { Link, useNavigate, useParams } from "react-router-dom";

function TesterProjectDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [approving, setApproving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [startingTesting, setStartingTesting] = useState(false);

  /* =========================
     PROJECT OBJECT
  ========================= */

  const project = data?.project || null;

  /* =========================
     LOAD PROJECT DETAILS
  ========================= */

  const loadProject = async () => {
    try {
      setLoading(true);

      if (!id) {
        setData(null);
        return;
      }

      const res = await axios.get(`/projects/${id}`);

      console.log("PROJECT DETAILS RESPONSE:", res);

      if (res?.code === 200) {
        setData(res.data);
      } else {
        setData(null);
      }
    } catch (error) {
      console.error(
        "PROJECT LOAD ERROR:",
        error?.response?.data || error
      );

      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProject();
  }, [id]);

  /* =========================
     START TESTING
  ========================= */

  const startTesting = async () => {
    try {
      setStartingTesting(true);

      const res = await axios.put(
        `/projects/start-testing/${id}`
      );

      if (res?.code === 200) {
        alert("Testing started successfully");
        await loadProject();
      }
    } catch (err) {
      console.error(err);

      alert(
        err?.response?.data?.message ||
          "Failed to start testing"
      );
    } finally {
      setStartingTesting(false);
    }
  };

  /* =========================
     FORMAT STATUS
  ========================= */

  const formatStatus = (status) => {
    if (!status) {
      return "-";
    }

    return status
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) =>
        char.toUpperCase()
      );
  };

  /* =========================
     STATUS BADGE
  ========================= */

  const getStatusClass = (status) => {
    switch (status) {
      case "assigned":
        return "bg-primary";

      case "in_testing":
        return "bg-info";

      case "bug_found":
        return "bg-danger";

      case "retesting":
        return "bg-warning text-dark";

      case "approved":
        return "bg-success";

      default:
        return "bg-secondary";
    }
  };

  /* =========================
     LOADING
  ========================= */

  if (loading) {
    return (
      <div className="content">
        <div className="text-center py-5">
          Loading Project...
        </div>
      </div>
    );
  }

  /* =========================
     PROJECT NOT FOUND
  ========================= */

  if (!project) {
    return (
      <div className="content">
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-5">

            <h4 className="mb-2">
              Project not found
            </h4>

            <p className="text-muted mb-3">
              This project may not exist or you
              may not have permission to access it.
            </p>

            <button
              className="btn btn-primary"
              onClick={() => navigate("/projects")}
            >
              Back to Projects
            </button>

          </div>
        </div>
      </div>
    );
  }

  /* =========================
     APPROVE PROJECT
  ========================= */

  const approveProject = async () => {
    const confirmed = window.confirm(
      "Are you sure all testing is complete and you want to approve this project?"
    );

    if (!confirmed) {
      return;
    }

    try {
      setApproving(true);

      const res = await axios.put(
        `/projects/approve-testing/${id}`
      );

      if (res?.code === 200) {
        alert("Project approved successfully");
        await loadProject();
      }
    } catch (err) {
      console.error(err);

      alert(
        err?.response?.data?.message ||
          "Failed to approve project"
      );
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="content">

      {/* =========================
          PAGE HEADER
      ========================= */}

      <div
        className="
          d-flex
          justify-content-between
          align-items-center
          mb-4
        "
      >
        <div>
          <h3 className="fw-bold mb-1">
            🧪 {project.projectName}
          </h3>

          <p className="text-muted mb-0">
            Project Testing Details
          </p>
        </div>

        <span
          className={`badge fs-6 px-3 py-2 ${getStatusClass(
            project.testingStatus
          )}`}
        >
          {formatStatus(project.testingStatus)}
        </span>
      </div>

      <div className="row">

        {/* =========================
            LEFT SIDE
        ========================= */}

        <div className="col-lg-8">

          {/* PROJECT DETAILS */}

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">

              <h5 className="mb-4">
                📁 Project Information
              </h5>

              <div className="row">

                <div className="col-md-6 mb-3">
                  <small className="text-muted">
                    Project Name
                  </small>

                  <div className="fw-semibold">
                    {project.projectName}
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <small className="text-muted">
                    Priority
                  </small>

                  <div className="fw-semibold text-capitalize">
                    {project.priority || "-"}
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <small className="text-muted">
                    Deadline
                  </small>

                  <div className="fw-semibold">
                    {project.deadline
                      ? new Date(
                          project.deadline
                        ).toLocaleDateString()
                      : "-"}
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <small className="text-muted">
                    Developer
                  </small>

                  <div className="fw-semibold">
                    {project?.assignedDeveloper?.name ||
                      "N/A"}
                  </div>
                </div>

              </div>

              {project.notes && (
                <div>
                  <small className="text-muted">
                    Project Notes
                  </small>

                  <div>
                    {project.notes}
                  </div>
                </div>
              )}

            </div>
          </div>

          {/* CLIENT DETAILS */}

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">

              <h5 className="mb-4">
                👤 Client Information
              </h5>

              <div className="row">

                <div className="col-md-6 mb-3">
                  <small className="text-muted">
                    Client Name
                  </small>

                  <div className="fw-semibold">
                    {project?.leadId?.name || "N/A"}
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <small className="text-muted">
                    Email
                  </small>

                  <div className="fw-semibold">
                    {project?.leadId?.email || "-"}
                  </div>
                </div>

                <div className="col-md-6">
                  <small className="text-muted">
                    Phone
                  </small>

                  <div className="fw-semibold">
                    {project?.leadId?.phone || "-"}
                  </div>
                </div>

              </div>

            </div>
          </div>

          {/* TESTING CREDENTIALS */}

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">

              <h5 className="mb-4">
                🔐 Testing Credentials
              </h5>

              {/* WEBSITE */}

              <div className="mb-3">
                <small className="text-muted">
                  Website URL
                </small>

                <div>
                  {project.testingUrl ? (
                    <a
                      href={project.testingUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {project.testingUrl}
                    </a>
                  ) : (
                    "-"
                  )}
                </div>
              </div>

              {/* ADMIN URL */}

              <div className="mb-3">
                <small className="text-muted">
                  Admin URL
                </small>

                <div>
                  {project.adminUrl ? (
                    <a
                      href={project.adminUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {project.adminUrl}
                    </a>
                  ) : (
                    "-"
                  )}
                </div>
              </div>

              {/* USERNAME */}

              <div className="mb-3">
                <small className="text-muted">
                  Username
                </small>

                <div>
                  {project.testingUsername || "-"}
                </div>
              </div>

              {/* PASSWORD */}

              <div className="mb-3">
                <small className="text-muted">
                  Password
                </small>

                <div>
                  {project.testingPassword || "-"}
                </div>
              </div>

              {/* NOTES */}

              <div>
                <small className="text-muted">
                  Testing Notes
                </small>

                <div>
                  {project.testingNotes || "-"}
                </div>
              </div>

            </div>
          </div>

        </div>

        {/* =========================
            RIGHT SIDE
        ========================= */}

        <div className="col-lg-4">

          {/* TESTING ACTION */}

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">

              <h5 className="mb-3">
                🧪 Testing Action
              </h5>

              {project.testingStatus ===
                "assigned" && (
                <>
                  <p className="text-muted">
                    This project is ready for testing.
                  </p>

                  <button
                    className="btn btn-primary w-100"
                    onClick={startTesting}
                    disabled={startingTesting}
                  >
                    {startingTesting
                      ? "Starting..."
                      : "Start Testing"}
                  </button>
                </>
              )}

              {project.testingStatus ===
                "in_testing" && (
                <div className="alert alert-info mb-0">
                  Testing is currently in progress.
                </div>
              )}

              {project.testingStatus ===
                "bug_found" && (
                <div className="alert alert-danger mb-0">
                  Bugs have been found. Waiting for
                  developer fixes.
                </div>
              )}

              {project.testingStatus ===
                "retesting" && (
                <>
                  <div className="alert alert-warning">
                    Bugs have been fixed by the
                    developer. Please retest the project.
                  </div>

                  <button
                    className="btn btn-warning w-100"
                    onClick={startTesting}
                    disabled={startingTesting}
                  >
                    {startingTesting
                      ? "Starting Retesting..."
                      : "Start Retesting"}
                  </button>
                </>
              )}

              {project.testingStatus ===
                "approved" && (
                <div className="alert alert-success mb-0">
                  Project testing has been approved.
                </div>
              )}

            </div>
          </div>

          {/* BUG MANAGEMENT */}

          <div className="card border-0 shadow-sm">
            <div className="card-body">

              <h5 className="mb-3">
                🐛 Bug Management
              </h5>

              <p className="text-muted">
                Manage and track all bugs found during
                testing.
              </p>

              <Link
                to={`/bugs?projectId=${id}`}
                className="btn btn-danger w-100"
              >
                Manage Bugs
              </Link>

            </div>
          </div>

          {/* FINAL APPROVAL */}

          <div className="card border-0 shadow-sm mt-4">
            <div className="card-body">

              <h5 className="mb-3">
                ✅ Final Approval
              </h5>

              <p className="text-muted">
                Approve this project only after all
                testing and bug verification is complete.
              </p>

              {project.testingStatus === "in_testing" ||
              project.testingStatus === "retesting" ? (
                <button
                  className="btn btn-success w-100"
                  onClick={approveProject}
                  disabled={approving}
                >
                  {approving
                    ? "Approving..."
                    : "Approve Project"}
                </button>
              ) : project.testingStatus ===
                "approved" ? (
                <div className="alert alert-success mb-0">
                  ✓ Project Approved Successfully
                </div>
              ) : (
                <div className="alert alert-secondary mb-0">
                  Complete testing and close all bugs
                  before approval.
                </div>
              )}

            </div>
          </div>

        </div>

      </div>

    </div>
  );
}

export default TesterProjectDetails;